import { useEffect, useRef, useState, type FormEvent, type ReactNode } from 'react';
import { UserRound } from 'lucide-react';
import {
  changeAgentClientCeiling,
  disableAgentClient,
  enableAgentClient,
  enableOperatorClient,
  listOperators,
  problemErrorCode,
  recertifyAgentClient,
  revokeAgentGrant,
  type AgentActiveGrant,
  type AgentClient,
  type AgentGrant,
  type AgentManagementReason,
  type AgentScope,
  type OperatorDto,
} from '@/lib/api';
import {
  DETAIL_MAX_LENGTH,
  MANAGEMENT_REASONS,
  clientStatusLabel,
  describeScopes,
  grantStatusLabel,
  isManagementReason,
  reasonLabel,
  revokeReasonLabel,
  scopesUpToLevel,
  validateDetail,
} from '@/lib/agents';
import {
  OPERATOR_INSTRUCTION,
  ceilingChangeLevels,
  ceilingLevelsForRole,
  operatorHighlights,
  roleScopesInEnvironment,
} from '@/lib/operator-access';
import { useAuthStore } from '@/stores/auth';
import { Badge } from './ui';

/**
 * Operadores no Claude Code, dentro de `/agentes` (change platform-mcp-operator-access, PR 8.3,
 * tarefa 4.1; OA-D1, OA-D3, OA-D5, OA-D6).
 *
 * Um Admin habilita a conta de um operador (cria o cliente `Operator`, um por conta e por
 * ambiente), escolhe o teto dentro do papel dela, recertifica, desabilita e revoga a concessão
 * pessoal. O consentimento é sempre do titular, em `/mcp/autorizar`; daqui ninguém aprova por
 * outra conta.
 *
 * Contrato da PR 8.1: a listagem traz `clientKind`, `subjectAccountId` e `subjectName`, sem o papel
 * do titular, que a seção lê da lista de contas (só quando há cliente pessoal ou o formulário
 * abre). O caminho do operador vem de `GET /platform/me/operator-access` (`settings`, tarefa 4.3):
 * desligado e sem cliente pessoal, a seção some; desligado com clientes, ela mostra o aviso e só o
 * que corta acesso (desabilitar e revogar). O `allowedScopes` do ambiente limita os níveis
 * oferecidos (em produção, só métricas). Sem essa leitura (API anterior), a seção aparece e aprende
 * o caminho desligado pelo `409 operator_disabled` da habilitação. A API revalida tudo; o mapa
 * papel → escopos daqui só evita oferecer teto que ela recusaria.
 */

export const OPERATOR_SECTION_TITLE = 'Operadores no Claude Code';
export const OPERATOR_DISABLED_NOTICE =
  'O caminho do operador está desligado neste ambiente: ninguém conecta agora. Os clientes abaixo podem ser desabilitados e as concessões revogadas.';
export const SELF_ENABLE_WARNING =
  'Você está habilitando a sua própria conta. A habilitação e a sua aprovação depois gravam auditoria High e alertam todos os Admins ativos.';

const OPERATOR_DISABLED_CODE = 'platform.agent_client.operator_disabled';

/**
 * Recusas próprias do cliente `Operator` (PR 8.1), só nesta seção: os mesmos códigos no registro de
 * um perfil do Hermes seguem com o texto da página e do servidor.
 */
const OPERATOR_ERROR_MESSAGES: Record<string, string> = {
  'platform.agent_client.invalid_scopes':
    'Teto fora do permitido: o operador só recebe escopos de leitura (nada de escrita, proposta ou acesso total).',
  'platform.agent_client.scope_above_role': 'O teto passa dos escopos do papel atual da conta titular.',
  'platform.agent_client.scope_not_allowed_in_environment':
    'Escopo não liberado neste ambiente (em produção, só métricas agregadas).',
  'platform.agent_client.subject_not_active':
    'A conta escolhida não pode ser habilitada: precisa estar ativa, sem revogação e sem bloqueio.',
  'platform.agent_client.profile_taken': 'Esta conta já tem o Claude Code habilitado neste ambiente.',
  'platform.agent_client.redirect_fixed': 'A URL de volta do operador é fixa (o Claude Code na máquina dele ou o claude.ai) e não muda.',
  [OPERATOR_DISABLED_CODE]: 'O acesso do operador está desligado neste ambiente.',
};

type OperatorAction =
  | { kind: 'ceiling'; client: AgentClient }
  | { kind: 'recertify'; client: AgentClient }
  | { kind: 'disable'; client: AgentClient }
  | { kind: 'enable'; client: AgentClient }
  | { kind: 'revoke'; client: AgentClient; grant: AgentActiveGrant };

interface OperatorActionCopy {
  title: string;
  confirmLabel: string;
  danger: boolean;
  /** A rota grava o motivo e o detalhe? Nas outras, o motivo só confirma a intenção. */
  sendsReason: boolean;
  description: string;
}

const OPERATOR_ACTION_COPY: Record<OperatorAction['kind'], OperatorActionCopy> = {
  ceiling: {
    title: 'Mudar o teto do operador',
    confirmLabel: 'Mudar teto',
    danger: false,
    sendsReason: true,
    description:
      'O teto fica sempre dentro do papel atual da conta. Baixar reduz a concessão pessoal na mesma hora; subir exige login recente e só vale depois de uma nova aprovação do titular.',
  },
  recertify: {
    title: 'Recertificar o operador',
    confirmLabel: 'Recertificar',
    danger: false,
    sendsReason: false,
    description:
      'Recertificar renova por mais um ciclo o direito do operador de conectar o Claude Code. Sem recertificação, o cliente pessoal não autoriza nem autentica. Exige login recente com senha e TOTP.',
  },
  disable: {
    title: 'Desabilitar o operador',
    confirmLabel: 'Desabilitar',
    danger: true,
    sendsReason: true,
    description:
      'Desabilitar corta o Claude Code do operador: a concessão pessoal é revogada e a próxima chamada recebe 401. Habilitar de novo não revive a concessão.',
  },
  enable: {
    title: 'Habilitar o operador de novo',
    confirmLabel: 'Habilitar',
    danger: false,
    sendsReason: false,
    description:
      'O cliente pessoal volta sem concessão: o titular precisa conectar e aprovar de novo, com o código do autenticador. Exige login recente com senha e TOTP.',
  },
  revoke: {
    title: 'Revogar a concessão pessoal',
    confirmLabel: 'Revogar',
    danger: true,
    sendsReason: true,
    description:
      'A concessão sai de uso na hora; a próxima chamada do Claude Code do operador recebe 401. Para voltar, o titular conecta e aprova de novo.',
  },
};

function formatDateTime(iso: string | null | undefined, empty = '—'): string {
  if (!iso) return empty;
  return new Date(iso).toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function subjectLabel(client: Pick<AgentClient, 'subjectName'>): string {
  return client.subjectName ?? 'conta removida';
}

type AccountsState = { status: 'idle' } | { status: 'loading' } | { status: 'failed' } | { status: 'ready'; items: OperatorDto[] };

export interface OperatorAccessSectionProps {
  /** Só os clientes `Operator` da listagem de `/agentes`. */
  clients: AgentClient[];
  /** A tag do ambiente da listagem (`stg`, `prd`), para o texto do `client_id` derivado. */
  environmentTag: string | null;
  grants: AgentGrant[];
  now: Date;
  /** Uma mutação da página está em curso. */
  busy: boolean;
  /** Depois de cada mudança: a página mostra o aviso e recarrega a lista. */
  onChanged: (notice: string) => void;
  /**
   * Recusa de uma mutação: a página trata a sessão, a configuração e a geração (e devolve `null`),
   * ou devolve a mensagem que o modal mostra.
   */
  onFailure: (err: unknown) => string | null;
  /**
   * O caminho do operador e o teto do ambiente, de `GET /platform/me/operator-access`. Ausente ou
   * `null` (API anterior à rota, ou a leitura falhou): a seção aparece e aprende o caminho desligado
   * pelo `409 operator_disabled`.
   */
  settings?: OperatorSettings | null;
}

/** O que a seção lê do ambiente: `enabled` e `allowedScopes` de `GET /platform/me/operator-access`. */
export interface OperatorSettings {
  enabled: boolean;
  allowedScopes: AgentScope[];
}

export function OperatorAccessSection({
  clients,
  environmentTag,
  grants,
  now,
  busy,
  onChanged,
  onFailure,
  settings = null,
}: OperatorAccessSectionProps) {
  const [enableOpen, setEnableOpen] = useState(false);
  const [pending, setPending] = useState<OperatorAction | null>(null);
  const [mutating, setMutating] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  // Aprendido do `409 operator_disabled`: a API não anuncia o caminho do operador por rota.
  const [pathDisabled, setPathDisabled] = useState(false);
  const [accounts, setAccounts] = useState<AccountsState>({ status: 'idle' });

  // A lista de contas (o papel de cada titular, que o DTO não traz) só é lida quando a seção
  // precisa dela: há cliente pessoal na tela ou o formulário de habilitação abriu.
  const needsAccounts = clients.length > 0 || enableOpen;
  const accountsRequested = useRef(false);
  useEffect(() => {
    if (!needsAccounts || accountsRequested.current) return;
    accountsRequested.current = true;
    setAccounts({ status: 'loading' });
    listOperators({ status: 'Active', pageSize: 100 })
      .then((page) => setAccounts({ status: 'ready', items: page.items }))
      .catch(() => setAccounts({ status: 'failed' }));
  }, [needsAccounts]);

  const accountItems = accounts.status === 'ready' ? accounts.items : null;
  const roleOf = (accountId: string | null | undefined) =>
    accountItems?.find((a) => a.id === accountId)?.role ?? null;
  const allowedScopes = settings?.allowedScopes ?? null;
  const scopesOf = (accountId: string | null | undefined) => roleScopesInEnvironment(roleOf(accountId), allowedScopes);
  const enabled = !pathDisabled && (settings?.enabled ?? true);

  /** A recusa de uma mutação: a página trata sessão e configuração; o resto ganha o texto daqui. */
  function operatorFailure(err: unknown): string | null {
    const code = problemErrorCode(err);
    if (code === OPERATOR_DISABLED_CODE) {
      setPathDisabled(true);
      setEnableOpen(false);
    }
    const message = onFailure(err);
    if (message === null) return null;
    return (code && Object.hasOwn(OPERATOR_ERROR_MESSAGES, code) && OPERATOR_ERROR_MESSAGES[code]) || message;
  }

  async function runAction(action: OperatorAction, input: OperatorActionInput) {
    setMutating(true);
    setActionError(null);
    const { client } = action;
    try {
      switch (action.kind) {
        case 'ceiling':
          await changeAgentClientCeiling(client.id, {
            expectedGeneration: client.generation,
            maxScopes: scopesUpToLevel(input.level ?? 0),
            reason: input.reason,
            detail: input.detail,
          });
          break;
        case 'recertify':
          await recertifyAgentClient(client.id, { expectedGeneration: client.generation });
          break;
        case 'disable':
          await disableAgentClient(client.id, {
            expectedGeneration: client.generation,
            reason: input.reason,
            detail: input.detail,
          });
          break;
        case 'enable':
          await enableAgentClient(client.id, { expectedGeneration: client.generation });
          break;
        case 'revoke':
          await revokeAgentGrant(action.grant.id, {
            expectedGeneration: action.grant.generation,
            reason: input.reason,
            detail: input.detail,
          });
          break;
      }
      setPending(null);
      onChanged(`${OPERATOR_ACTION_COPY[action.kind].title}: feito para ${subjectLabel(client)}.`);
    } catch (err) {
      const message = operatorFailure(err);
      if (message === null) setPending(null);
      else setActionError(message);
    } finally {
      setMutating(false);
    }
  }

  const locked = busy || mutating;

  // Desligado pela API e sem nenhum cliente pessoal no ambiente: não há o que habilitar nem o que
  // cortar, e a seção some (a oferta seria recusada com `409 operator_disabled`).
  if (settings && !settings.enabled && clients.length === 0) return null;

  return (
    <section className="card" aria-label={OPERATOR_SECTION_TITLE} data-testid="operator-access-section">
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <UserRound size={18} />
        <div className="card-title">{OPERATOR_SECTION_TITLE}</div>
        <span style={{ flex: 1 }} />
        {enabled && !enableOpen && (
          <button type="button" className="btn btn-sm" disabled={locked} onClick={() => setEnableOpen(true)}>
            Habilitar operador
          </button>
        )}
      </div>
      <p className="muted" style={{ margin: '6px 0 10px', fontSize: 13 }}>
        Identidade pessoal, só de leitura, para o operador usar o MCP do backoffice no Claude Code da própria máquina.
        O teto nunca passa do papel atual da conta, e só o titular aprova a conexão. Concessão de até 30 dias, com
        renovação só nas 8 horas seguintes a cada aprovação. Só funciona com o acesso do operador ligado neste
        ambiente; desligado, a habilitação é recusada.
      </p>
      <p className="muted" style={{ margin: '0 0 10px', fontSize: 13 }} data-testid="operator-instruction">
        {OPERATOR_INSTRUCTION}
      </p>

      {!enabled && (
        <div className="info-banner" role="status">
          {OPERATOR_DISABLED_NOTICE}
        </div>
      )}

      {accounts.status === 'failed' && (
        <div className="error-banner" role="alert">
          Não foi possível carregar as contas de operador; sem o papel de cada conta, o teto não pode ser escolhido.
        </div>
      )}

      {enabled && enableOpen && (
        <EnableOperatorForm
          accounts={accountItems}
          environmentTag={environmentTag}
          allowedScopes={allowedScopes}
          existingAccountIds={new Set(clients.map((c) => c.subjectAccountId).filter((id): id is string => !!id))}
          onCancel={() => setEnableOpen(false)}
          onEnabled={(created) => {
            setEnableOpen(false);
            onChanged(`Operador ${subjectLabel(created)} habilitado. ${OPERATOR_INSTRUCTION}`);
          }}
          onFailure={operatorFailure}
        />
      )}

      {clients.length === 0 && !enableOpen && (
        <p className="muted" style={{ margin: 0 }}>
          Nenhum operador habilitado neste ambiente.
        </p>
      )}

      {clients.map((client) => (
        <OperatorClientCard
          key={client.id}
          client={client}
          history={grants.filter((g) => g.agentClientId === client.id && g.id !== client.activeGrant?.id)}
          now={now}
          role={roleOf(client.subjectAccountId)}
          canRaise={enabled}
          busy={locked}
          ceilingLevels={ceilingChangeLevels(client, scopesOf(client.subjectAccountId))}
          onAction={(action) => {
            setActionError(null);
            setPending(action);
          }}
        />
      ))}

      {pending && (
        <OperatorActionModal
          action={pending}
          levels={
            pending.kind === 'ceiling'
              ? ceilingChangeLevels(pending.client, scopesOf(pending.client.subjectAccountId))
              : []
          }
          submitting={mutating}
          error={actionError}
          onCancel={() => {
            setPending(null);
            setActionError(null);
          }}
          onConfirm={(input) => void runAction(pending, input)}
        />
      )}
    </section>
  );
}

// -----------------------------------------------------------------------------
//  Cartão de um operador
// -----------------------------------------------------------------------------

function OperatorClientCard({
  client,
  history,
  now,
  role,
  canRaise,
  busy,
  ceilingLevels,
  onAction,
}: {
  client: AgentClient;
  history: AgentGrant[];
  now: Date;
  /** Papel atual do titular, lido da lista de contas; nulo enquanto carrega ou se a conta saiu. */
  role: string | null;
  /** Com o caminho desligado, nada se amplia: só desabilitar e revogar. */
  canRaise: boolean;
  busy: boolean;
  ceilingLevels: number[];
  onAction: (action: OperatorAction) => void;
}) {
  const enabled = client.status === 'Enabled';
  const grant = client.activeGrant;
  const highlights = operatorHighlights(client, now);
  const name = subjectLabel(client);

  return (
    <article
      aria-label={`Operador ${name}`}
      data-testid={`operator-client-${client.subjectAccountId ?? client.id}`}
      style={{ borderTop: '1px solid var(--color-border, #e2e8f0)', paddingTop: 12, marginTop: 12 }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 8 }}>
        <strong>{name}</strong>
        {role && <Badge tone="info">{role}</Badge>}
        <Badge tone={enabled ? 'success' : 'neutral'} dot>
          {clientStatusLabel(client.status)}
        </Badge>
        {highlights.map((h) => (
          <Badge key={h.kind} tone="warning">
            <span data-testid={`operator-highlight-${h.kind}`}>{h.label}</span>
          </Badge>
        ))}
      </div>

      <dl style={{ display: 'grid', gridTemplateColumns: 'max-content 1fr', gap: '4px 16px', margin: 0, fontSize: 13 }}>
        <dt className="muted">Cliente OAuth</dt>
        <dd style={{ margin: 0 }} className="mono">
          {client.oauthClientId}
        </dd>
        <dt className="muted">Teto</dt>
        <dd style={{ margin: 0 }}>{describeScopes(client.maxScopes)}</dd>
        <dt className="muted">Recertificação</dt>
        <dd style={{ margin: 0 }}>até {formatDateTime(client.certificationDueAt)}</dd>
        {!enabled && (
          <>
            <dt className="muted">Desabilitado</dt>
            <dd style={{ margin: 0 }}>
              {formatDateTime(client.disabledAt)} · {reasonLabel(client.disabledReason)}
            </dd>
          </>
        )}
        <dt className="muted">Concessão ativa</dt>
        <dd style={{ margin: 0 }}>
          {grant ? (
            <>
              {describeScopes(grant.effectiveScopes)} · aprovada em {formatDateTime(grant.approvedAt)} · vence{' '}
              {formatDateTime(grant.expiresAt)} · último uso {formatDateTime(grant.lastUsedAt, 'nunca')}
            </>
          ) : (
            'nenhuma'
          )}
        </dd>
      </dl>

      <div className="btn-row" style={{ marginTop: 10 }}>
        {enabled && (
          <>
            {canRaise && (
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                disabled={busy || ceilingLevels.length === 0}
                title={ceilingLevels.length === 0 ? 'O papel da conta não permite outro teto neste ambiente' : undefined}
                onClick={() => onAction({ kind: 'ceiling', client })}
              >
                Mudar teto
              </button>
            )}
            {canRaise && (
              <button type="button" className="btn btn-secondary btn-sm" disabled={busy} onClick={() => onAction({ kind: 'recertify', client })}>
                Recertificar
              </button>
            )}
            {grant && (
              <button
                type="button"
                className="btn btn-danger btn-sm"
                disabled={busy}
                onClick={() => onAction({ kind: 'revoke', client, grant })}
              >
                Revogar concessão
              </button>
            )}
            <button type="button" className="btn btn-danger btn-sm" disabled={busy} onClick={() => onAction({ kind: 'disable', client })}>
              Desabilitar
            </button>
          </>
        )}
        {!enabled && canRaise && (
          <button type="button" className="btn btn-secondary btn-sm" disabled={busy} onClick={() => onAction({ kind: 'enable', client })}>
            Habilitar
          </button>
        )}
      </div>

      {history.length > 0 && (
        <details style={{ marginTop: 10 }}>
          <summary style={{ cursor: 'pointer', fontSize: 13 }}>Histórico de concessões ({history.length})</summary>
          <div className="table-scroll">
            <table aria-label={`Histórico de concessões de ${name}`}>
              <thead>
                <tr>
                  <th>Status</th>
                  <th>Escopos</th>
                  <th>Emitida</th>
                  <th>Saiu de uso</th>
                  <th>Motivo</th>
                </tr>
              </thead>
              <tbody>
                {history.map((g) => (
                  <tr key={g.id}>
                    <td>{grantStatusLabel(g.status)}</td>
                    <td>{describeScopes(g.scopes)}</td>
                    <td>{formatDateTime(g.issuedAt)}</td>
                    <td>{formatDateTime(g.revokedAt)}</td>
                    <td>
                      {revokeReasonLabel(g.revokeReason)}
                      {g.managementReason ? ` · ${reasonLabel(g.managementReason)}` : ''}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </details>
      )}
    </article>
  );
}

// -----------------------------------------------------------------------------
//  Habilitar operador
// -----------------------------------------------------------------------------

function EnableOperatorForm({
  accounts,
  environmentTag,
  allowedScopes,
  existingAccountIds,
  onCancel,
  onEnabled,
  onFailure,
}: {
  /** As contas ativas, lidas pela seção; nulo enquanto carrega. */
  accounts: OperatorDto[] | null;
  environmentTag: string | null;
  /** O teto do ambiente, quando a API o informou; nulo deixa só o papel. */
  allowedScopes: AgentScope[] | null;
  existingAccountIds: Set<string>;
  onCancel: () => void;
  onEnabled: (client: AgentClient) => void;
  onFailure: (err: unknown) => string | null;
}) {
  const me = useAuthStore((s) => s.operator?.id ?? null);
  const [accountId, setAccountId] = useState('');
  const [level, setLevel] = useState<number | null>(null);
  const [reason, setReason] = useState('');
  const [detail, setDetail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Só contas ativas, sem cliente pessoal neste ambiente e com algum escopo no papel que o ambiente
  // libera.
  const scopesFor = (role: string) => roleScopesInEnvironment(role, allowedScopes);
  const candidates = (accounts ?? []).filter(
    (a) => !existingAccountIds.has(a.id) && ceilingLevelsForRole(scopesFor(a.role)).length > 0,
  );
  const account = candidates.find((a) => a.id === accountId) ?? null;
  const levels = account ? ceilingLevelsForRole(scopesFor(account.role)) : [];
  const detailError = validateDetail(detail);
  const canSubmit =
    account !== null &&
    level !== null &&
    levels.includes(level) &&
    isManagementReason(reason) &&
    detailError === null &&
    !submitting;

  function chooseAccount(id: string) {
    setAccountId(id);
    const chosen = candidates.find((a) => a.id === id);
    const options = chosen ? ceilingLevelsForRole(scopesFor(chosen.role)) : [];
    // O menor nível vem marcado: o Admin sobe se precisar.
    setLevel(options.length > 0 ? options[0] : null);
  }

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (!canSubmit || !account || level === null || !isManagementReason(reason)) return;
    setSubmitting(true);
    setError(null);
    try {
      const created = await enableOperatorClient({
        subjectAccountId: account.id,
        maxScopes: scopesUpToLevel(level),
        reason,
        detail,
      });
      onEnabled({ ...created, subjectName: created.subjectName ?? account.fullName });
    } catch (err) {
      const message = onFailure(err);
      if (message !== null) setError(message);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <form onSubmit={submit} aria-label="Habilitar operador no Claude Code" style={{ marginBottom: 12 }}>
      <p className="muted" style={{ marginTop: 0, fontSize: 13 }}>
        O id do cliente é derivado pelo servidor (op-{environmentTag ?? 'ambiente'}-conta), com a volta só para o
        Claude Code na máquina do operador ou para o conector do claude.ai dele. Habilitar exige login recente com senha e TOTP.
      </p>
      <div className="grid-halves">
        <div className="field">
          <label htmlFor="operator-enable-account">Conta do operador</label>
          <select
            id="operator-enable-account"
            value={accountId}
            onChange={(e) => chooseAccount(e.target.value)}
            disabled={!accounts}
          >
            <option value="">{accounts ? 'Escolha a conta' : 'Carregando…'}</option>
            {candidates.map((a) => (
              <option key={a.id} value={a.id}>
                {a.fullName} · {a.role}
              </option>
            ))}
          </select>
        </div>
        <div className="field">
          <label htmlFor="operator-enable-level">Teto (dentro do papel da conta)</label>
          <select
            id="operator-enable-level"
            value={level ?? ''}
            onChange={(e) => setLevel(e.target.value ? Number(e.target.value) : null)}
            disabled={!account}
          >
            {!account && <option value="">Escolha a conta primeiro</option>}
            {levels.map((option) => (
              <option key={option} value={option}>
                {describeScopes(scopesUpToLevel(option))}
              </option>
            ))}
          </select>
        </div>
        <div className="field">
          <label htmlFor="operator-enable-reason">Motivo</label>
          <select id="operator-enable-reason" value={reason} onChange={(e) => setReason(e.target.value)}>
            <option value="">Escolha o motivo</option>
            {MANAGEMENT_REASONS.map((r) => (
              <option key={r.value} value={r.value}>
                {r.label}
              </option>
            ))}
          </select>
        </div>
      </div>
      <div className="field">
        <label htmlFor="operator-enable-detail">Detalhe (opcional, até {DETAIL_MAX_LENGTH} caracteres)</label>
        <textarea
          id="operator-enable-detail"
          value={detail}
          onChange={(e) => setDetail(e.target.value)}
          rows={2}
          aria-invalid={detailError !== null}
        />
        {detailError && (
          <div role="alert" style={{ fontSize: 12, color: 'var(--color-error)' }}>
            {detailError}
          </div>
        )}
      </div>
      {account && account.id === me && (
        <div className="warning-banner" role="note" style={{ marginBottom: 12 }}>
          {SELF_ENABLE_WARNING}
        </div>
      )}
      {error && (
        <div className="error-banner" role="alert" style={{ marginBottom: 12 }}>
          {error}
        </div>
      )}
      <div className="btn-row">
        <button type="submit" className="btn" disabled={!canSubmit}>
          {submitting ? 'Habilitando…' : 'Habilitar'}
        </button>
        <button type="button" className="btn btn-secondary" onClick={onCancel} disabled={submitting}>
          Cancelar
        </button>
      </div>
    </form>
  );
}

// -----------------------------------------------------------------------------
//  Confirmação das ações
// -----------------------------------------------------------------------------

interface OperatorActionInput {
  reason: AgentManagementReason;
  detail?: string;
  level?: number;
}

function OperatorActionModal({
  action,
  levels,
  submitting,
  error,
  onCancel,
  onConfirm,
}: {
  action: OperatorAction;
  levels: number[];
  submitting: boolean;
  error: string | null;
  onCancel: () => void;
  onConfirm: (input: OperatorActionInput) => void;
}) {
  const copy = OPERATOR_ACTION_COPY[action.kind];
  const name = subjectLabel(action.client);
  const [acknowledged, setAcknowledged] = useState(false);
  const [reason, setReason] = useState('');
  const [detail, setDetail] = useState('');
  const [level, setLevel] = useState<number | null>(null);
  const needsLevel = action.kind === 'ceiling';
  const detailError = copy.sendsReason ? validateDetail(detail) : null;
  const canConfirm =
    acknowledged &&
    isManagementReason(reason) &&
    detailError === null &&
    (!needsLevel || (level !== null && levels.includes(level))) &&
    !submitting;

  function submit(event: FormEvent) {
    event.preventDefault();
    if (!canConfirm || !isManagementReason(reason)) return;
    onConfirm({
      reason,
      detail: copy.sendsReason ? detail : undefined,
      level: needsLevel ? (level ?? undefined) : undefined,
    });
  }

  return (
    <div
      role="presentation"
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(15,23,42,0.6)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 40,
      }}
    >
      <form
        role="dialog"
        aria-modal="true"
        aria-labelledby="operator-action-title"
        onSubmit={submit}
        style={{
          background: '#fff',
          borderRadius: 12,
          padding: 24,
          maxWidth: 480,
          width: '90%',
          maxHeight: '90vh',
          overflowY: 'auto',
          boxShadow: '0 24px 48px rgba(0,0,0,0.25)',
        }}
      >
        <h2 id="operator-action-title" style={{ marginTop: 0, fontSize: 18 }}>
          {copy.title}
        </h2>
        <p style={{ fontSize: 13 }}>{copy.description}</p>
        <dl style={{ display: 'grid', gridTemplateColumns: 'max-content 1fr', gap: '4px 12px', fontSize: 13 }}>
          <Fact label="Operador">{name}</Fact>
          <Fact label="Cliente OAuth">
            <span className="mono">{action.client.oauthClientId}</span>
          </Fact>
          <Fact label="Teto atual">{describeScopes(action.client.maxScopes)}</Fact>
        </dl>

        {needsLevel && (
          <div className="field">
            <label htmlFor="operator-action-level">Novo teto</label>
            <select
              id="operator-action-level"
              value={level ?? ''}
              onChange={(e) => setLevel(e.target.value ? Number(e.target.value) : null)}
            >
              <option value="">Escolha o nível</option>
              {levels.map((option) => (
                <option key={option} value={option}>
                  {describeScopes(scopesUpToLevel(option))}
                </option>
              ))}
            </select>
          </div>
        )}

        <div className="field">
          <label htmlFor="operator-action-reason">Motivo</label>
          <select id="operator-action-reason" value={reason} onChange={(e) => setReason(e.target.value)}>
            <option value="">Escolha o motivo</option>
            {MANAGEMENT_REASONS.map((r) => (
              <option key={r.value} value={r.value}>
                {r.label}
              </option>
            ))}
          </select>
          {!copy.sendsReason && (
            <div className="muted" style={{ fontSize: 12 }}>
              Esta rota não grava o motivo; ele só confirma a intenção.
            </div>
          )}
        </div>

        {copy.sendsReason && (
          <div className="field">
            <label htmlFor="operator-action-detail">Detalhe (opcional, até {DETAIL_MAX_LENGTH} caracteres)</label>
            <textarea
              id="operator-action-detail"
              value={detail}
              onChange={(e) => setDetail(e.target.value)}
              rows={2}
              aria-invalid={detailError !== null}
            />
            {detailError && (
              <div role="alert" style={{ fontSize: 12, color: 'var(--color-error)' }}>
                {detailError}
              </div>
            )}
          </div>
        )}

        <label style={{ display: 'flex', gap: 8, alignItems: 'flex-start', fontSize: 13, margin: '8px 0 12px' }}>
          <input type="checkbox" checked={acknowledged} onChange={(e) => setAcknowledged(e.target.checked)} />
          <span>Confirmo a ação no cliente pessoal de {name}.</span>
        </label>

        {error && (
          <div className="error-banner" role="alert" style={{ marginBottom: 12 }}>
            {error}
          </div>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button type="button" className="btn btn-secondary" onClick={onCancel} disabled={submitting}>
            Cancelar
          </button>
          <button type="submit" className={copy.danger ? 'btn btn-danger' : 'btn'} disabled={!canConfirm}>
            {submitting ? 'Confirmando…' : copy.confirmLabel}
          </button>
        </div>
      </form>
    </div>
  );
}

function Fact({ label, children }: { label: string; children: ReactNode }) {
  return (
    <>
      <dt className="muted">{label}</dt>
      <dd style={{ margin: 0 }}>{children}</dd>
    </>
  );
}
